import { TriangleAlert } from 'components/icons';
import { useFormatDate } from 'components/misc/FormattedDate';
import { useDebounce } from '@uidotdev/usehooks';
import { useMemo } from 'react';
import { useGetNominationWarningsQuery } from 'store/api/rankingsReferees';
import { ClassifierData } from 'types/classifiers';
import { NominationLine, NominationWarning, NominationWarningsRequest } from 'types/efforts';
import { getClassifierText, t } from 'utils/getTranslations';
import { nominationName } from './nominationName';

/** The warnings endpoint answers whoever may write a BD effort. */
export const NOMINATION_WARNINGS_PERMISSIONS = ['CREATE.EFFORTS', 'UPDATE.EFFORTS.ALL', 'UPDATE.EFFORTS.MY_OWN'];

type WarningsArgs = Omit<NominationWarningsRequest, 'lines'> & { lines: NominationLine[] };

/** Each referee's warning, asked once the request has stayed put for a moment, and never for lines with no referee. */
export function useNominationWarnings({ lines, ...effort }: WarningsArgs, skip = false): NominationWarning[] {
    const request: NominationWarningsRequest = {
        ...effort,
        lines: lines.map(({ legalDirectoryCode, editionYear, referees }) => ({
            legalDirectoryCode,
            editionYear,
            partyIds: referees.map(({ party }) => party.id),
        })),
    };
    const sentKey = useDebounce(JSON.stringify(request), 400);
    const sent = useMemo(() => JSON.parse(sentKey) as NominationWarningsRequest, [sentKey]);
    const { data } = useGetNominationWarningsQuery(sent, {
        skip: skip || !sent.lines.some(({ partyIds }) => partyIds.length > 0),
    });
    return data ?? [];
}

/** A referee's warning as one sentence, the same in the effort form and on the effort's page. */
export function useNominationWarningText(directories: ClassifierData | undefined) {
    const formatDate = useFormatDate();
    return (name: string, warning: NominationWarning): string => {
        const date = formatDate(warning.requestedOn) ?? '';
        // A deleted colleague's name comes back blank: the sentence then names no one.
        const by = warning.requestedBy?.name?.trim() ?? '';
        const author = by ? '' : '-no-author';
        const directory = getClassifierText(directories, warning.legalDirectoryCode);
        return warning.type === 'ALREADY_NOMINATED'
            ? t(`efforts.nominations.warning-already${author}`, {
                  name,
                  nomination: nominationName(directory, warning.editionYear, warning.practiceArea?.name),
                  by,
                  date,
              })
            : t(`efforts.nominations.warning-recent${author}`, {
                  name,
                  date,
                  nomination: nominationName(directory, warning.editionYear),
                  by,
              });
    };
}

/** A warning under its referee's name. */
export const NominationWarningNote = ({ text }: { text: string }) => (
    <p className="text-warning-darker new:text-warning-ink m-0 flex items-start gap-1.5 text-xs">
        <TriangleAlert className="mt-px size-3.5 shrink-0" />
        <span className="text-pretty">{text}</span>
    </p>
);
